import { type SortingState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

export type GetSortingColId<SortId extends string> =
  SortId extends `${infer ColId}:${string}` ? ColId : never;

export type GetSortingDir<SortId extends string> =
  SortId extends `${string}:${infer SortDir}` ? SortDir : never;

export type GetSortingState<SortId extends string> = {
  id: GetSortingColId<SortId>;
  desc: boolean;
}[];

/**
 * This hook should only be used when the table is sorted by one column at a time
 * so enableMultiSort option should be false for the table. It accepts a generic type
 * that should encompass all possible column ids by which the sorting can be done.
 * @param defaultSorting - The default column id to sort by.
 * @param defaultSortDirection - The default sort direction.
 * @param tableToApiIdMap - An map of column ids to sort ids. If provided, it will be used instead of the mapper functions.
 */
export const useServerSorting = <
  SortOption extends string,
  TableColumnId extends string = SortOption,
>(
  defaultSorting?: GetSortingColId<SortOption>,
  desc?: boolean,
  tableToApiIdMap?: Record<TableColumnId, GetSortingColId<SortOption>>,
) => {
  type ApiId = GetSortingColId<SortOption>;
  type TableId = TableColumnId;

  const apiToTableIdMap = useMemo(
    () =>
      (tableToApiIdMap
        ? Object.fromEntries(
            Object.entries(tableToApiIdMap).map(([key, val]) => [val, key]),
          )
        : {}) as Record<ApiId, TableId>,
    [tableToApiIdMap],
  );

  const getTableId = (apiId: ApiId): string => {
    return apiToTableIdMap[apiId] ?? apiId;
  };

  const getApiId = (tableId: TableId): string => {
    return tableToApiIdMap?.[tableId] ?? tableId;
  };

  const newSortingState: SortingState = useMemo(() => {
    if (!defaultSorting) {
      return [];
    }
    const columnId = getTableId(defaultSorting) as TableId;
    return [{ id: columnId, desc: !!desc }];
  }, [defaultSorting, desc]);

  const [sortingState, setSortingState] =
    useState<SortingState>(newSortingState);

  // Update sorting state only when defaultSorting changes and differs from current state
  useEffect(() => {
    const currentId = sortingState[0]?.id;
    const currentDesc = sortingState[0]?.desc;
    const newId = newSortingState[0]?.id;
    const newDesc = newSortingState[0]?.desc;
    // Only update if there's an actual change
    if (currentId !== newId || currentDesc !== newDesc) {
      setSortingState(newSortingState);
    }
  }, [newSortingState]);

  const sortBy = useMemo(() => {
    if (!sortingState?.[0]) {
      return undefined;
    }
    const columnId = sortingState[0].id as TableColumnId;
    const sortDirection = sortingState[0].desc ? 'desc' : 'asc';
    const sortId = getApiId(columnId) as ApiId;

    if (!sortId) {
      return undefined;
    }

    return `${sortId}:${sortDirection}` as SortOption;
  }, [sortingState?.[0]?.id, sortingState?.[0]?.desc, getApiId]);

  return { sortBy, sortingState, setSortingState };
};
